import {
  Component,
  inject,
  signal,
  OnDestroy,
  QueryList,
  ViewChildren,
  ElementRef,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { StorageService } from '../../services/storage';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [ReactiveFormsModule, DialogModule, RouterLink, PasswordModule],
  templateUrl: './forgot-password.html',
  styleUrls: ['./forgot-password.css', '../login/auth-hero.css'],
})
export class ForgotPassword implements OnDestroy {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private storage = inject(StorageService);
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);

  // ---- reset modal state ----
  showResetModal = signal(false);
  resetLoading = signal(false);
  resendLoading = signal(false);
  expired = signal(false);
  countdown = signal('05:00');

  private expiryMinutes = 5;
  private interval: any;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // Identifier = username / email / mobile number.
  forgotForm: FormGroup = this.fb.group({
    identifier: ['', Validators.required],
  });

  resetForm: FormGroup = this.fb.group({
    otp: this.fb.array(
      Array.from({ length: 6 }, () =>
        this.fb.control('', { nonNullable: true, validators: Validators.required }),
      ),
    ),
    newPassword: ['', [Validators.required, Validators.minLength(6)]],
  });

  get otpArray(): FormArray<FormControl<string>> {
    return this.resetForm.get('otp') as FormArray<FormControl<string>>;
  }

  get newPassword() {
    return this.resetForm.get('newPassword');
  }

  // ================= STEP 1: SEND OTP =================
  onSendOtp() {
    if (this.forgotForm.invalid) {
      this.forgotForm.markAllAsTouched();
      this.toast.warning('Please enter your username, email or mobile number');
      return;
    }

    this.loading.set(true);
    const identifier = this.forgotForm.value.identifier;

    this.authService.sendOtp(identifier).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success) {
          this.expiryMinutes = Number(res?.data?.expiry_minutes ?? 5) || 5;
          this.toast.success('OTP sent successfully!');
          this.openResetModal();
        } else {
          this.toast.error(res?.message || 'Failed to send OTP');
        }
      },
      error: () => {
        this.loading.set(false);
        // errorInterceptor already surfaces a toast
      },
    });
  }

  private openResetModal() {
    this.otpArray.reset();
    this.newPassword?.reset();
    this.expired.set(false);
    this.showResetModal.set(true);
    this.startCountdown();
  }

  closeResetModal() {
    this.showResetModal.set(false);
    clearInterval(this.interval);
  }

  onResetModalShow() {
    this.otpInputs.get(0)?.nativeElement.focus();
  }

  // ================= OTP INPUT UX =================
  onInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const digit = target.value.replace(/\D/g, '').slice(0, 1);
    this.otpArray.at(index).setValue(digit);
    target.value = digit;
    if (digit && index < 5) {
      this.otpInputs.get(index + 1)?.nativeElement.focus();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpArray.at(index).value && index > 0) {
      this.otpInputs.get(index - 1)?.nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (pasted.length === 6) {
      pasted.split('').forEach((val, i) => this.otpArray.at(i).setValue(val));
      this.otpInputs.get(5)?.nativeElement.focus();
    }
  }

  private getOtp(): string {
    return this.otpArray.value.join('');
  }

  // ================= STEP 2: RESET PASSWORD =================
  onResetPassword() {
    if (this.resetForm.invalid) {
      if (this.otpArray.invalid) {
        this.toast.warning('Please enter the complete 6-digit OTP');
      } else {
        this.toast.warning('New password must be at least 6 characters');
      }
      this.resetForm.markAllAsTouched();
      return;
    }

    const identifier = this.forgotForm.value.identifier;
    const otp = Number(this.getOtp());
    const newPassword = this.newPassword?.value;

    this.resetLoading.set(true);

    this.authService.resetPassword(identifier, otp, newPassword).subscribe({
      next: (res: any) => {
        this.resetLoading.set(false);
        if (res?.success === true) {
          this.closeResetModal();
          // Force a clean re-login with the new password.
          this.storage.clearAll();
          this.toast.success('Password changed successfully. Please login again.');
          this.router.navigate(['/login']);
        } else {
          this.toast.error(res?.message || 'Invalid or expired OTP');
        }
      },
      error: () => {
        this.resetLoading.set(false);
        // errorInterceptor already surfaces a toast
      },
    });
  }

  // ================= RESEND OTP =================
  resendOtp() {
    if (this.resendLoading()) return;

    const identifier = this.forgotForm.value.identifier;
    if (!identifier) {
      this.toast.warning('Identifier is missing. Please close and try again.');
      return;
    }

    this.resendLoading.set(true);

    this.authService.resendOtp(identifier).subscribe({
      next: (res: any) => {
        this.resendLoading.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to resend OTP');
          return;
        }
        this.otpArray.reset();
        this.startCountdown();
        this.otpInputs.get(0)?.nativeElement.focus();
        this.toast.success('OTP resent successfully');
      },
      error: () => {
        this.resendLoading.set(false);
        // errorInterceptor already surfaces a toast
      },
    });
  }

  // ================= COUNTDOWN =================
  private startCountdown() {
    clearInterval(this.interval);
    this.expired.set(false);
    if (!isPlatformBrowser(this.platformId)) return;

    const endTime = Date.now() + this.expiryMinutes * 60 * 1000;

    const update = () => {
      const diff = endTime - Date.now();
      if (diff <= 0) {
        clearInterval(this.interval);
        this.countdown.set('00:00');
        this.expired.set(true);
        this.toast.warning('OTP expired. Tap "Resend OTP" to get a new code.');
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.countdown.set(
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      );
    };

    update();
    this.interval = setInterval(update, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.interval);
  }
}
